import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import Button from './Button';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const existingConsent = Cookies.get('cookie-consent');
    if (!existingConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const fullConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    
    saveConsent(fullConsent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    
    saveConsent(minimalConsent);
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
    setIsVisible(false);
    setShowSettings(false);
  };

  const saveConsent = (consentData: CookieConsent) => {
    // Save consent to cookie (expires in 1 year)
    Cookies.set('cookie-consent', JSON.stringify(consentData), { 
      expires: 365,
      secure: true,
      sameSite: 'Strict'
    });

    // Here you would typically initialize/disable tracking services based on consent
    if (consentData.analytics) {
      // Initialize analytics (e.g., Google Analytics)
      console.log('Analytics enabled');
    }

    if (consentData.marketing) {
      // Initialize marketing tools
      console.log('Marketing cookies enabled');
    }
  };

  const handleConsentChange = (type: keyof CookieConsent) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-dark-surface border-t border-dark-border/30 shadow-2xl"
      >
        <div className="max-w-7xl mx-auto p-6">
          {!showSettings ? (
            // Main cookie banner
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-cream mb-2">
                  {t('cookies.title')}
                </h3>
                <p className="text-cream-dark text-sm lg:text-base">
                  {t('cookies.message')}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 lg:ml-6">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="ghost"
                  size="sm"
                  className="text-sm"
                >
                  {t('cookies.settings')}
                </Button>
                
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  size="sm"
                  className="text-sm"
                >
                  {t('cookies.reject')}
                </Button>
                
                <Button
                  onClick={handleAcceptAll}
                  variant="primary"
                  size="sm"
                  className="text-sm"
                >
                  {t('cookies.accept')}
                </Button>
              </div>
            </div>
          ) : (
            // Cookie settings panel
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-cream mb-2">
                  {t('cookies.settings')}
                </h3>
                <p className="text-cream-dark">
                  {t('cookies.settingsDescription')}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-dark-primary/50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-cream">
                      {t('cookies.necessary')}
                    </h4>
                    <p className="text-sm text-cream-dark mt-1">
                      {t('cookies.necessaryDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={consent.necessary}
                      disabled
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-dark-primary/50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-cream">
                      {t('cookies.analytics')}
                    </h4>
                    <p className="text-sm text-cream-dark mt-1">
                      {t('cookies.analyticsDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={() => handleConsentChange('analytics')}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-dark-primary/50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-cream">
                      {t('cookies.marketing')}
                    </h4>
                    <p className="text-sm text-cream-dark mt-1">
                      {t('cookies.marketingDescription')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={() => handleConsentChange('marketing')}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
{t('cookies.back')}
                </Button>
                
                <Button
                  onClick={handleSaveSettings}
                  variant="primary"
                  size="sm"
                >
{t('cookies.saveSettings')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;